import { useMemo, useState } from "react";
import { Plus, Printer, Trash2, Pencil } from "lucide-react";
import { DishDetailsDialog, dishBody } from "@/components/DishDetails";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field } from "@/components/kit";
import { MenuShareCard } from "@/components/MenuShareCard";
import { printAllergenMatrix } from "@/lib/allergenPrint";
import { ArrangeMenuButton } from "@/components/ArrangeMenu";
import { orderedDishes } from "@/lib/menuOrder";

export const ALLERGENS = ["celery", "gluten", "crustaceans", "eggs", "fish", "lupin", "milk", "molluscs", "mustard", "nuts", "peanuts", "sesame", "soya", "sulphites"];

export default function Allergens() {
  const { can, business } = useAuth();
  const { data, reload, setData } = useApi("/dishes");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [details, setDetails] = useState(null);
  const edit = can("manage");
  const { data: menu, reload: reloadMenu } = useApi("/allergen-menu/settings");
  const order = useMemo(() => menu?.category_order || [], [menu]);
  const dishes = useMemo(() => orderedDishes(data || [], order), [data, order]);

  const toggle = async (dish, a) => {
    const allergens = dish.allergens.includes(a) ? dish.allergens.filter((x) => x !== a) : [...dish.allergens, a];
    const body = dishBody(dish, { allergens, may_contain: (dish.may_contain || []).filter((x) => x !== a) });
    setData(data.map((d) => (d.id === dish.id ? { ...d, allergens, may_contain: body.may_contain } : d)));
    try {
      await api.put(`/dishes/${dish.id}`, body);
    } catch (e) {
      toast.error(errMsg(e));
      reload();
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/dishes", { name, category, allergens: [], notes: "", description: "", may_contain: [] });
      setName(""); setCategory("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name}?`)) return;
    await api.delete(`/dishes/${d.id}`);
    reload();
  };

  return (
    <div>
      <PageHeader eyebrow="Natasha's Law · 14 allergens" title="Allergen matrix" subtitle="Tap a cell to mark an allergen as present. Every change is recorded in the audit trail."
        actions={<div className="flex flex-wrap gap-2">{edit && <ArrangeMenuButton dishes={dishes} order={order} onSaved={() => { reloadMenu(); reload(); }} />}<button data-testid="print-allergens-button" onClick={() => { if (!printAllergenMatrix(dishes, business?.name || "")) toast.error("Allow pop-ups to print"); }} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold hover:border-slate-900"><Printer className="h-4 w-4" />Print</button></div>} />
      <MenuShareCard />
      {edit && (
        <form onSubmit={add} className="no-print mb-6 flex flex-wrap items-end gap-3">
          <Field label="Dish" className="min-w-[220px] flex-1"><input data-testid="dish-name-input" required className="ka-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Category" className="w-48"><input data-testid="dish-category-input" className="ka-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Mains" /></Field>
          <button data-testid="add-dish-button" className="ka-press inline-flex h-[42px] items-center gap-1 rounded-full bg-emerald-600 px-5 text-sm font-semibold text-white hover:bg-emerald-700"><Plus className="h-4 w-4" />Add dish</button>
        </form>
      )}
      <div className="ka-card overflow-x-auto">
        <table data-testid="allergen-matrix-table" className="w-full min-w-[900px] text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 border-b border-slate-200 bg-white px-4 py-3 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">Dish</th>
              {ALLERGENS.map((a) => <th key={a} className="border-b border-slate-200 px-1 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-500"><div className="mx-auto w-4 whitespace-nowrap [writing-mode:vertical-rl] rotate-180">{a}</div></th>)}
              <th className="border-b border-slate-200" />
            </tr>
          </thead>
          <tbody>
            {dishes.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50/60">
                <td className="sticky left-0 border-b border-slate-100 bg-white px-4 py-2.5"><div className="font-semibold text-slate-900">{d.name}</div>{d.description && <div className="max-w-[260px] truncate text-xs text-slate-600">{d.description}</div>}<div className="text-xs text-slate-500">{d.category} · updated {fmtD(d.updated_at || d.created_at)}</div></td>
                {ALLERGENS.map((a) => {
                  const on = d.allergens.includes(a);
                  const may = !on && (d.may_contain || []).includes(a);
                  return (
                    <td key={a} className="border-b border-slate-100 p-1 text-center">
                      <button data-testid={`allergen-cell-${d.id}-${a}`} disabled={!edit} onClick={() => toggle(d, a)} title={may ? "May contain (set in dish details)" : ""}
                        className={`ka-press h-8 w-8 rounded-lg text-xs font-bold ${on ? "bg-rose-600 text-white" : may ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300" : "bg-slate-100 text-slate-300 hover:bg-slate-200"} disabled:cursor-default`}>{on ? "✓" : may ? "M" : "·"}</button>
                    </td>
                  );
                })}
                <td className="whitespace-nowrap border-b border-slate-100 px-2">{edit && <button data-testid={`edit-dish-${d.id}`} onClick={() => setDetails(d)} title="Description and may contain" className="no-print ka-press rounded-md p-2 text-slate-400 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>}{edit && <button data-testid={`delete-dish-${d.id}`} onClick={() => remove(d)} className="no-print ka-press rounded-md p-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
            {data && !data.length && <tr><td className="p-6 text-slate-500" colSpan={16}>No dishes yet.</td></tr>}
          </tbody>
        </table>
        {details && <DishDetailsDialog dish={details} onClose={() => setDetails(null)} onSaved={() => { setDetails(null); reload(); }} />}
      </div>
    </div>
  );
}
